import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, RefreshCw, Copy, Clock, MessageSquare } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, useToast } from '@hiring-intelligence/ui';
import { jdApi, feedbackApi } from '@/services/api';

export default function JDGeneratorPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [isEditing, setIsEditing] = useState(false);
	const [editedContent, setEditedContent] = useState('');
	const [showFeedbackForm, setShowFeedbackForm] = useState(false);
	const [feedbackText, setFeedbackText] = useState('');

	const { data, isLoading } = useQuery({
		queryKey: ['job-description', id],
		queryFn: () => jdApi.get(id!)
	});

	const jd = data?.data;
	const currentVersion = jd?.versions?.find((v: any) => v.id === jd?.currentVersionId) || jd?.versions?.[0];

	const updateMutation = useMutation({
		mutationFn: () => jdApi.update(id!, editedContent, currentVersion?.structuredContent || {}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['job-description', id] });
			toast({ title: 'Saved', description: 'Job description updated' });
			setIsEditing(false);
		}
	});

	const regenerateMutation = useMutation({
		mutationFn: () => jdApi.regenerate(id!),
		onSuccess: () => {
			toast({ title: 'Regenerating', description: 'Regenerating the JD...' });
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['job-description', id] });
			}, 5000);
		}
	});

	const feedbackMutation = useMutation({
		mutationFn: () => feedbackApi.create(id!, {
			jdVersionId: currentVersion?.id,
			source: 'manual',
			rawFeedback: feedbackText
		}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['job-description', id] });
			toast({ title: 'Feedback Added', description: 'Feedback saved successfully' });
			setShowFeedbackForm(false);
			setFeedbackText('');
		}
	});

	const handleCopy = () => {
		navigator.clipboard.writeText(currentVersion?.content || '');
		toast({ title: 'Copied', description: 'JD copied to clipboard' });
	};

	const startEditing = () => {
		setEditedContent(currentVersion?.content || '');
		setIsEditing(true);
	};

	if (isLoading) {
		return <div className="p-4 md:p-8 text-center">Loading...</div>;
	}

	if (!jd) {
		return <div className="p-4 md:p-8 text-center">Job description not found</div>;
	}

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
				<div className="flex items-center gap-4">
					<Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
						<ArrowLeft className="h-5 w-5" />
					</Button>
					<div>
						<h1 className="text-2xl font-bold">Job Description</h1>
						<p className="text-muted-foreground">
							Version {currentVersion?.versionNumber || 1} •
							{currentVersion?.generationMethod === 'ai_generated' ? ' Auto Generated' : ' Manual Edit'}
						</p>
					</div>
				</div>
				<div className="flex gap-2">
					{isEditing ? (
						<>
							<Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
							<Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
								<Save className="mr-2 h-4 w-4" />
								Save
							</Button>
						</>
					) : (
						<>
							<Button variant="outline" onClick={handleCopy}>
								<Copy className="mr-2 h-4 w-4" />
								Copy
							</Button>
							<Button variant="outline" onClick={startEditing}>
								Edit
							</Button>
							<Button
								variant="outline"
								onClick={() => regenerateMutation.mutate()}
								disabled={regenerateMutation.isPending}
							>
								<RefreshCw className="mr-2 h-4 w-4" />
								Regenerate
							</Button>
							<Button onClick={() => setShowFeedbackForm(true)}>
								<MessageSquare className="mr-2 h-4 w-4" />
								Add Feedback
							</Button>
						</>
					)}
				</div>
			</div>

			<div className="grid gap-6 lg:grid-cols-3">
				{/* Main Content */}
				<div className="lg:col-span-2">
					<Card>
						<CardContent className="p-6">
							{isEditing ? (
								<textarea
									className="w-full min-h-[600px] p-4 border rounded-md font-mono text-sm"
									value={editedContent}
									onChange={(e) => setEditedContent(e.target.value)}
								/>
							) : (
								<div className="prose prose-sm max-w-none">
									<pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
										{currentVersion?.content || 'No content generated yet.'}
									</pre>
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Sidebar */}
				<div className="space-y-4">
					{/* Version History */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Clock className="h-5 w-5" />
								Version History
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-2">
								{jd.versions?.map((version: any) => (
									<div
										key={version.id}
										className={`p-3 rounded border ${version.id === currentVersion?.id ? 'border-primary bg-primary/5' : ''}`}
									>
										<div className="flex items-center justify-between">
											<span className="font-medium">v{version.versionNumber}</span>
											<span className="text-xs text-muted-foreground">
												{new Date(version.createdAt).toLocaleDateString()}
											</span>
										</div>
										<p className="text-xs text-muted-foreground mt-1">
											{version.generationMethod === 'ai_generated' ? 'Auto Generated' :
												version.generationMethod === 'ai_regenerated' ? 'Regenerated' : 'Manual Edit'}
										</p>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					{/* Feedback */}
					{showFeedbackForm && (
						<Card>
							<CardHeader>
								<CardTitle>Add Feedback</CardTitle>
							</CardHeader>
							<CardContent>
								<textarea
									className="w-full p-3 border rounded-md text-sm min-h-[150px]"
									placeholder="Enter Hiring Manager feedback..."
									value={feedbackText}
									onChange={(e) => setFeedbackText(e.target.value)}
								/>
								<div className="flex gap-2 mt-3">
									<Button onClick={() => feedbackMutation.mutate()} disabled={!feedbackText.trim()}>
										Submit
									</Button>
									<Button variant="outline" onClick={() => setShowFeedbackForm(false)}>
										Cancel
									</Button>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Existing Feedback */}
					{jd.feedbacks?.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<MessageSquare className="h-5 w-5" />
									Feedback ({jd.feedbacks.length})
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{jd.feedbacks.map((fb: any) => (
										<div key={fb.id} className="p-3 bg-muted rounded">
											<p className="text-sm">{fb.rawFeedback}</p>
											{fb.aiSummary && (
												<div className="mt-2 pt-2 border-t">
													<p className="text-xs font-medium">Summary:</p>
													<p className="text-xs text-muted-foreground">{fb.aiSummary}</p>
												</div>
											)}
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
